"use client";

import { c as _c } from "react/compiler-runtime";
import { useTenant } from "@/contexts/tenant-provider";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import type { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import { CreateNoteState } from "@/types/enums/note-state";
import { useScribeStore } from "@/stores/scribe-store";

/**
 * Switches the global recording slice to a telehealth Note once its Recall
 * bot is admitted.
 *
 * When a provider clicks "Join meeting" for a session whose bot has no Note
 * yet — a fresh visit, or a *rejoin* after the prior bot ended — the backend
 * spawns the bot but defers Note creation to the Recall webhook (it
 * materializes the Note on bot admission). `AppointmentItem` records the
 * spawned bot id via `setPendingTelehealthHydration` and then navigates the
 * provider away, so the `TELEHEALTH_BOT_STATUS` alert carrying the new Note
 * id lands *after* that component has unmounted.
 *
 * This hook lives in the always-mounted `RecordingPiP`, so it still receives
 * that alert post-navigation and points the PIP at the new Note. The
 * component-local subscription it replaces was torn down on unmount, which is
 * why a rejoin used to leave the PIP stuck on the prior (ended) note.
 *
 * We match the alert's `recallBotId` to the exact bot the provider just
 * spawned. One event can have several bots/Notes (rejoins), so matching by
 * event alone latches onto a sibling Note. The bot id is unambiguous.
 */
export function useTelehealthHydration() {
  const $ = _c(5);
  const {
    tenant
  } = useTenant();
  let t0;
  if ($[0] !== tenant?.enabledModules) {
    t0 = tenant?.enabledModules.includes("RECALL") ?? false;
    $[0] = tenant?.enabledModules;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hasRecallModule = t0;
  const pending = useScribeStore(_temp);
  const pendingBotId = pending?.recallBotId;
  const enabled = !!pendingBotId && hasRecallModule;
  let t1;
  if ($[2] !== pendingBotId) {
    t1 = function handleMessage(message) {
      if (!pendingBotId) {
        return;
      }
      if (message.type !== AlertType.TELEHEALTH_BOT_STATUS) {
        return;
      }
      const payload = message.data as {
        eventId?: string;
        noteId?: string | null;
        recallBotId?: string | null;
      };
      if (payload?.recallBotId !== pendingBotId) {
        return;
      }
      if (!payload?.noteId) {
        return;
      }
      const store = useScribeStore.getState();
      store.setRecordingId(payload.noteId);
      store.setAudioSource("telehealth");
      store.setRecordingNoteState(CreateNoteState.RECORDING);
      store.setPendingTelehealthHydration(undefined);
    };
    $[2] = pendingBotId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleMessage = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [AlertType.TELEHEALTH_BOT_STATUS];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useWebSocketCallbackSubscription(t2, handleMessage, enabled, "TelehealthHydration");
}
function _temp(s) {
  return s.pendingTelehealthHydration;
}